// src/utils/bodyFatEstimator.js
// US Navy Method body fat estimation + projection logic

/**
 * Estimate body fat % using the US Navy circumference method.
 * All inputs in inches.
 *
 * Men:   BF% = 86.010 × log10(waist - neck) - 70.041 × log10(height) + 36.76
 * Women: BF% = 163.205 × log10(waist + hip - neck) - 97.684 × log10(height) - 78.387
 *
 * Returns null if inputs are invalid.
 */
export function estimateBodyFat({ sex, neck_inches, waist_inches, hip_inches, height_inches }) {
  if (!neck_inches || !waist_inches || !height_inches) return null;
  if (neck_inches <= 0 || height_inches <= 0)           return null;

  let bf;
  if (sex === "male") {
    const diff = waist_inches - neck_inches;
    if (diff <= 0) return null;
    bf = 86.010 * Math.log10(diff) - 70.041 * Math.log10(height_inches) + 36.76;
  } else {
    if (!hip_inches || hip_inches <= 0) return null;
    const sum = waist_inches + hip_inches - neck_inches;
    if (sum <= 0) return null;
    bf = 163.205 * Math.log10(sum) - 97.684 * Math.log10(height_inches) - 78.387;
  }

  // Clamp to physiologically possible range
  return Math.max(3, Math.min(60, Math.round(bf * 10) / 10));
}

/**
 * Project body composition (fat mass vs lean mass) over time.
 *
 * Assumptions:
 *  - Weekly weight loss comes from the calorie deficit
 *  - Muscle preservation factor: high protein (≥0.8g/lb) → 80% fat loss
 *                                low protein (<0.8g/lb)  → 65% fat loss
 *  - Returns array of weekly data points
 *
 * @param {object} params
 * @param {number} params.current_weight_lbs
 * @param {number} params.body_fat_pct         - e.g. 22 (not 0.22)
 * @param {number} params.goal_weight_lbs
 * @param {number} params.weekly_loss_lbs      - e.g. 1.0
 * @param {number} params.protein_per_lb       - e.g. 0.8
 * @returns {Array<{ week, weight, fat_mass, lean_mass, body_fat_pct }>}
 */
export function projectBodyComposition({
  current_weight_lbs,
  body_fat_pct,
  goal_weight_lbs,
  weekly_loss_lbs = 1.0,
  protein_per_lb  = 0.8,
}) {
  if (!body_fat_pct || !current_weight_lbs) return [];

  const fatFraction   = protein_per_lb >= 0.8 ? 0.80 : 0.65;
  const muscleFrac    = 1 - fatFraction;

  let weight   = current_weight_lbs;
  let fat_mass = (body_fat_pct / 100) * weight;
  let lean_mass = weight - fat_mass;

  const points = [];
  let week = 0;

  while (weight > goal_weight_lbs && week <= 52) {
    points.push({
      week,
      weight:       Math.round(weight * 10) / 10,
      fat_mass:     Math.round(fat_mass * 10) / 10,
      lean_mass:    Math.round(lean_mass * 10) / 10,
      body_fat_pct: Math.round((fat_mass / weight) * 1000) / 10,
    });

    const loss       = Math.min(weekly_loss_lbs, weight - goal_weight_lbs);
    const fat_loss   = loss * fatFraction;
    const muscle_loss = loss * muscleFrac;

    weight    -= loss;
    fat_mass  -= fat_loss;
    lean_mass -= muscle_loss;
    week++;
  }

  // Add goal week
  points.push({
    week,
    weight:       Math.round(weight * 10) / 10,
    fat_mass:     Math.round(fat_mass * 10) / 10,
    lean_mass:    Math.round(lean_mass * 10) / 10,
    body_fat_pct: Math.round((fat_mass / weight) * 1000) / 10,
  });

  return points;
}
